// MaterialCollectionsBlock — finishes picker. One row per collection
// attached to the active series; each row is a label + swatch grid.
//
// Tap semantics (per .design.md):
//   - Single tap: stages the material as the "selected" swatch within its
//     collection (visual feedback only in v1; step 8 will plumb a
//     pendingMaterialOverride scene-feature so new drops pick up the staged
//     default).
//   - Double tap: applies the material to every placed instance whose
//     surfaces include it. The host's applyMaterialToAll walks instances +
//     fires SetSurfaceMaterials per match.
//
// Renders nothing when there's no active series, the series has no
// collections, or every collection resolved empty. The slot the layout
// places this in is generally also bracketed by text-block headings whose
// `showWhen` already handles "Configure default products" vs. "Configure
// current selection" labelling — this block is presentation only.

import { useEffect, useRef, useState } from "react";
import type { BlockHostContext, BlockUniversalProps } from "./types";

export interface MaterialCollectionsBlockProps extends BlockUniversalProps {
  host: BlockHostContext;
}

// Tracks the "selected swatch" per collection — keyed by collectionId so each
// row's selection is independent. Selection is presentation-only in v1.
type StagedMap = Record<string, string>;

const DOUBLE_TAP_WINDOW_MS = 350;

export function MaterialCollectionsBlock({ themeVariant, className, host }: MaterialCollectionsBlockProps) {
  // Read the series-attached collections from the wire payload. v1: we pass
  // them through DesignerSeriesDto.materialCollections — the host's
  // activeSeries already has the data. Casts are safe because the layout's
  // host context type matches the wire shape one-to-one.
  const collections =
    (host.activeSeries?.materialCollections ?? []) as Array<{
      id: string;
      label: string;
      materials: Array<{
        id: string;
        dmatAssetId: string | null;
        name: string;
        swatchUrl: string | null;
      }>;
    }>;

  const [staged, setStaged] = useState<StagedMap>({});
  // Material the user is currently hovering, keyed by collection. Surfaces
  // the material name beside the row label so users can see what they are
  // about to pick without a per-swatch caption underneath every swatch.
  const [hovered, setHovered] = useState<StagedMap>({});
  const lastTapRef = useRef<{ collectionId: string; materialId: string; at: number } | null>(null);

  // Clear staged selections when the active series changes — they're
  // series-scoped.
  const activeSeriesId = host.activeSeries?.id ?? null;
  useEffect(() => {
    setStaged({});
  }, [activeSeriesId]);

  if (collections.length === 0) return null;

  const tap = (collectionId: string, materialId: string, dmatAssetId: string | null) => {
    (window as any).DConfigAnalytics?.track?.("material.changed", { surfaceKey: collectionId, materialId, previousMaterialId: host.collectionPicks[collectionId]?.materialId ?? null });
    const now = performance.now();
    const last = lastTapRef.current;
    const isDouble =
      last != null &&
      last.collectionId === collectionId &&
      last.materialId === materialId &&
      now - last.at < DOUBLE_TAP_WINDOW_MS;

    if (isDouble) {
      // Double-tap: apply to every placed instance with a matching surface.
      // Reset the tap tracker so a third quick tap doesn't compound.
      lastTapRef.current = null;
      host.applyMaterialToAll(collectionId, materialId, dmatAssetId);
      setStaged(prev => ({ ...prev, [collectionId]: materialId }));
      return;
    }

    // Single tap: this is the row's new default — drives new spawns via the
    // drag layer, and (when something's selected on a surface using this
    // collection) immediately applies to that selection too. host.
    // onPickCollectionDefault owns both effects.
    lastTapRef.current = { collectionId, materialId, at: now };
    setStaged(prev => ({ ...prev, [collectionId]: materialId }));
    host.onPickCollectionDefault(collectionId, materialId, dmatAssetId);
  };

  return (
    <div
      className={"sd-block-material-collections" + (className ? " " + className : "")}
      data-variant={themeVariant}
    >
      {collections.map(c => {
        // The "selected swatch" prefers the host-level pick (so a default set
        // earlier still shows as selected if this block re-mounts), with the
        // block-local staged map as a fallback while the host pick hydrates.
        const hostPicked = host.collectionPicks[c.id]?.materialId;
        const selectedMaterialId = hostPicked ?? staged[c.id];
        // The header text shows the hovered material's name while the cursor is
        // over a swatch, otherwise the collection label (e.g. "Wood"). It does
        // NOT append a chip — the label text itself swaps. No fallback to the
        // selected material so the header reads "Wood" again once the cursor
        // leaves, matching the design.
        const hoveredName = hovered[c.id]
          ? c.materials.find(m => m.id === hovered[c.id])?.name ?? ""
          : "";
        const headerText = hoveredName || c.label;
        return (
          <section key={c.id} className="sd-block-material-collections__row">
            <header className="sd-block-material-collections__row-header">
              <span className="sd-block-material-collections__row-label">{headerText}</span>
            </header>
            <div className="sd-block-material-collections__swatches">
              {c.materials.map(m => {
                const isSelected = selectedMaterialId === m.id;
                return (
                  <div
                    key={m.id}
                    className={
                      "sd-block-material-collections__swatch-item" +
                      (isSelected
                        ? " sd-block-material-collections__swatch-item--selected"
                        : "")
                    }
                  >
                    <button
                      type="button"
                      className={
                        "sd-block-material-collections__swatch" +
                        (isSelected ? " sd-block-material-collections__swatch--selected" : "")
                      }
                      data-track-key="options.material-swatch"
                      onClick={() => tap(c.id, m.id, m.dmatAssetId)}
                      onMouseEnter={() => setHovered(prev => ({ ...prev, [c.id]: m.id }))}
                      onMouseLeave={() =>
                        setHovered(prev => {
                          if (prev[c.id] !== m.id) return prev;
                          const next = { ...prev };
                          delete next[c.id];
                          return next;
                        })
                      }
                      title={m.name + " — double-click to apply to every placed product"}
                      aria-label={m.name}
                      aria-pressed={isSelected}
                    >
                      {m.swatchUrl ? (
                        <img src={m.swatchUrl} alt="" draggable={false} />
                      ) : (
                        <span className="sd-block-material-collections__swatch-blank" />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
